import React, { useContext, useState } from 'react'

import { Button, Form, Input, P, TextArea } from '@driftt/tide-core'
import { Modal, ModalBody, ModalFooter,ModalTitle } from '@driftt/tide-modal'

import { CurrentUserContext } from 'context/currentUserContext'
import { segmentTrack,TrackType } from 'utils/tracking'

import styles from './styles.module.css'

interface FeedbackModalProps {
  onClose: () => void
  onSubmit: () => void
}

const DISAPPOINTMENT = ['Very disappointed', 'Somewhat disappointed', 'Not disappointed']

const DIFFICULTY = ['Very difficult', 'Somewhat difficult', 'Neutral', 'Somewhat easy', 'Very easy']

const FormValidation: React.FC = () => {
  return <span className={styles.formValidation}>This question is required</span>
}

const CCRMFeedbackModal: React.FC<FeedbackModalProps> = ({ onClose, onSubmit }) => {
  const currentUser = useContext(CurrentUserContext)
  const [disappointment, setDisappointment] = useState<string | undefined>()
  const [difficulty, setDifficulty] = useState<string | undefined>()
  const [feedback, setFeedback] = useState<string | undefined>()
  const [futureResearchOptIn, setFutureResearchOptIn] = useState<string | undefined>()
  const [submittedInvalid, setSubmittedInvalid] = useState<boolean>(false)

  const onTextChange = (e: any) => {
    setFeedback(e.target.value)
  }

  const dismissAndClose = () => {
    onClose()
  }

  const handleSubmit = () => {
    if (!(disappointment && difficulty && futureResearchOptIn)) {
      setSubmittedInvalid(true)
      return
    }

    segmentTrack(TrackType.SUBMITTED_FEEDBACK_FORM, {
      email: currentUser?.email,
      orgId: currentUser?.orgId,
      disappointment,
      rating: difficulty,
      feedback,
      futureResearchOptIn,
      userId: currentUser?.id
    })
    onSubmit()
    dismissAndClose()
  }

  return (
    <Modal open={true} size="medium" onClose={dismissAndClose} className={styles.feedbackModal}>
      <ModalTitle className={styles.modalHeader}>Give feedback</ModalTitle>
      <ModalBody className={styles.modalBody}>
        <Form className={styles.feedbackForm} onSubmit={handleSubmit}>
          <P className={styles.prompt}>
            <span className={styles.semiBold}>
              How would you feel if you could no longer use the new version of Drift’s account
              activity tools?
            </span>
            {submittedInvalid && !disappointment && <FormValidation />}
          </P>
          <div className={styles.radioButtons}>
            {DISAPPOINTMENT.map(value => (
              <Input
                type="radio"
                key={value}
                className={styles.radio}
                label={value}
                onClick={() => setDisappointment(value)}
                checked={disappointment === value}
              />
            ))}
          </div>
          <P className={styles.prompt}>
            <span className={styles.semiBold}>
              How difficult or easy is it to use the new version of Drift’s account activity tools?
            </span>
            {submittedInvalid && !difficulty && <FormValidation />}
          </P>
          <div className={styles.radioButtons}>
            {DIFFICULTY.map(value => (
              <Input
                type="radio"
                key={value}
                className={styles.radio}
                label={value}
                onClick={() => setDifficulty(value)}
                checked={difficulty === value}
              />
            ))}
          </div>
          <P>
            <span className={styles.semiBold}>
              How can we make Drift’s account activity tools even better for you?
            </span>{' '}
            (Optional)
          </P>
          <TextArea
            className={styles.feedbackInput}
            placeholder="It would be great if I could..."
            onChange={onTextChange}
            value={feedback}
          />
          <P className={styles.futureResearchPrompt}>
            <span className={styles.semiBold}>
              {"Are you interested in participating in future research on Drift's tools?"}
            </span>
            {submittedInvalid && !futureResearchOptIn && <FormValidation />}
          </P>
          <div className={styles.futureResearchInput}>
            {['Yes', 'No'].map(value => (
              <Input
                type="radio"
                key={value}
                className={styles.radio}
                label={value}
                onClick={() => setFutureResearchOptIn(value)}
                checked={futureResearchOptIn === value}
              />
            ))}
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button type="primary" onClick={handleSubmit} className={styles.submitButton}>
          Submit
        </Button>
        <Button type="tertiary" onClick={dismissAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default CCRMFeedbackModal
